import { useEffect, useState } from "react";
import { http, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { ArrowCounterClockwise, CalendarBlank } from "@phosphor-icons/react";

/**
 * Dialog shown to admin / project manager when they click "Reopen" on a
 * completed task. Forces them to set a new due date — because the previous
 * one is almost always in the past once a task is being re-worked.
 */
export default function ReopenTaskDialog({ task, open, onOpenChange, onDone }) {
  const [newDue, setNewDue] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      // Default: 7 days from today — a sensible nudge, user can change.
      const d = new Date();
      d.setDate(d.getDate() + 7);
      setNewDue(d.toISOString().slice(0, 10));
      setNote("");
    }
  }, [open]);

  const submit = async () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(newDue)) {
      toast.error("Pick a new due date.");
      return;
    }
    setBusy(true);
    try {
      // Step 1: patch due_date (and append the reason to notes if provided).
      const payload = { due_date: newDue };
      if (note.trim()) {
        const prefix = task.notes ? `${task.notes}\n\n` : "";
        payload.notes = `${prefix}Reopened on ${new Date().toISOString().slice(0, 10)} — ${note.trim()}`;
      }
      await http.patch(`/tasks/${task.id}`, payload);
      // Step 2: move status to in_progress.
      await http.patch(`/tasks/${task.id}/status`, { status: "in_progress" });
      toast.success(`Task reopened. New due date: ${newDue}`);
      onDone?.();
      onOpenChange?.(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="reopen-task-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowCounterClockwise size={20} weight="duotone" className="text-amber-700" />
            Reopen Task
          </DialogTitle>
        </DialogHeader>

        <div className="text-sm text-zinc-600 mt-1">
          <p className="font-semibold text-zinc-800">{task?.title}</p>
          {task?.project && (
            <p className="text-xs font-mono text-zinc-500 mt-1">
              {task.project}{task.client_name ? ` · ${task.client_name}` : ""}
            </p>
          )}
        </div>

        <div className="mt-4">
          <label className="text-xs font-semibold tracking-wider uppercase text-zinc-500 mb-1 block">
            <CalendarBlank size={12} weight="bold" className="inline mr-1" />
            New Due Date <span className="text-red-600">*</span>
          </label>
          <Input
            type="date"
            data-testid="reopen-due-date"
            value={newDue}
            onChange={(e) => setNewDue(e.target.value)}
            min={new Date().toISOString().slice(0, 10)}
          />
          {task?.due_date && (
            <p className="text-xs text-zinc-500 mt-1">
              Previously due <span className="font-mono">{String(task.due_date).slice(0, 10)}</span>
            </p>
          )}
        </div>

        <div className="mt-3">
          <label className="text-xs font-semibold tracking-wider uppercase text-zinc-500 mb-1 block">
            Reason / Change Note (optional)
          </label>
          <Input
            data-testid="reopen-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Client requested revised layout"
          />
          <p className="text-xs text-zinc-400 mt-1">Appended to the task notes so the assignee sees it.</p>
        </div>

        <DialogFooter className="mt-5">
          <Button variant="outline" onClick={() => onOpenChange?.(false)} data-testid="reopen-cancel">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={busy}
            data-testid="reopen-confirm"
            className="bg-amber-700 hover:bg-amber-800 text-white gap-2"
          >
            <ArrowCounterClockwise size={14} weight="bold" />
            {busy ? "Reopening…" : "Reopen & Set New Due Date"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
